import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'
import {ArrowRight, CheckCircle2, Mail, Sparkles} from 'lucide-react'

interface SuccessState {
  name?: string
  discount?: number
  code?: string
  loginUrl?: string | null
}

const COUNTDOWN_SECONDS = 10

function Success() {
  const location = useLocation()
  const state = (location.state as SuccessState) ?? {}
  const loginUrl = state.loginUrl ?? null

  const [secondsLeft, setSecondsLeft] = useState<number>(COUNTDOWN_SECONDS)
  const [paused, setPaused] = useState<boolean>(false)
  const redirectedRef = useRef<boolean>(false)

  const goToLogin = () => {
    if (redirectedRef.current || !loginUrl) return
    redirectedRef.current = true
    window.location.href = loginUrl
  }

  useEffect(() => {
    if (!loginUrl || paused) return
    if (secondsLeft <= 0) {
      goToLogin()
      return
    }
    const timer = window.setTimeout(() => {
      setSecondsLeft((s) => s - 1)
    }, 1000)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, paused, loginUrl])

  const progress = Math.max(0, Math.min(100, (secondsLeft / COUNTDOWN_SECONDS) * 100))

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 relative overflow-hidden flex items-center justify-center px-5 py-10">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[32rem] h-[32rem] bg-sky-300/40 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[24rem] h-[24rem] bg-teal-300/30 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 max-w-md w-full text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-20 h-20 bg-gradient-to-br from-sky-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-sky-500/30"
        >
          <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={2} />
        </motion.div>

        <h1 className="text-3xl font-bold mb-3 text-slate-900">
          {state.name ? `Pronto, ${state.name.split(' ')[0]}!` : 'Cadastro confirmado!'}
        </h1>
        <p className="text-slate-600 mb-6">
          Sua recompensa foi resgatada com sucesso. Seu acesso exclusivo já está pronto.
        </p>

        {state.code ? (
          <div className="bg-white/80 backdrop-blur-xl border border-slate-200 rounded-2xl p-5 mb-6 shadow-md">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Recompensa resgatada</p>
            <p className="font-mono font-bold text-xl tracking-wider bg-gradient-to-r from-sky-600 to-teal-600 bg-clip-text text-transparent mb-2">{state.code}</p>
            {state.discount ? (
              <div className="inline-flex items-center gap-1 px-3 py-1 bg-sky-100 border border-sky-200 rounded-full text-sky-700 text-sm">
                <Sparkles className="w-3 h-3" />
                {state.discount}% de desconto
              </div>
            ) : null}
          </div>
        ) : null}

        {loginUrl ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="bg-white/90 backdrop-blur-xl border border-sky-200 rounded-2xl p-5 mb-6 shadow-lg shadow-sky-500/10"
          >
            <button
              type="button"
              onClick={goToLogin}
              className="group w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-bold py-3.5 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-sky-500/30"
            >
              Acessar minha conta agora
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>

            <div className="mt-4 text-left">
              <p className="text-sm text-slate-700 leading-relaxed">
                {paused ? (
                  <>Redirecionamento <span className="font-semibold text-slate-900">pausado</span>. Clique no botão acima quando estiver pronto. ✨</>
                ) : (
                  <>
                    Você será redirecionado automaticamente em{' '}
                    <span className="font-bold text-sky-600 tabular-nums">{secondsLeft}s</span>{' '}
                    com login automático — sem precisar digitar senha. ✨
                  </>
                )}
              </p>

              <div className="mt-3 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-sky-500 to-teal-500 transition-[width] duration-1000 ease-linear"
                  style={{ width: paused ? '100%' : `${progress}%` }}
                />
              </div>

              <button
                type="button"
                onClick={() => setPaused((p) => !p)}
                className="mt-3 text-xs font-medium text-slate-500 hover:text-slate-700 transition"
              >
                {paused ? 'Retomar redirecionamento' : 'Cancelar redirecionamento'}
              </button>
            </div>
          </motion.div>
        ) : (
          <div className="bg-white/80 backdrop-blur-xl border border-slate-200 rounded-2xl p-5 mb-6 shadow-md">
            <div className="flex items-start gap-3 text-left">
              <div className="w-9 h-9 shrink-0 rounded-lg bg-sky-100 flex items-center justify-center">
                <Mail className="w-4 h-4 text-sky-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900 mb-1">Tudo certo!</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Em instantes você receberá seu link de acesso exclusivo no e-mail cadastrado.
                </p>
              </div>
            </div>
          </div>
        )}

        <Link
          to="/"
          className="inline-block text-sky-600 hover:text-sky-700 text-sm font-medium transition"
        >
          ← Voltar ao início
        </Link>
      </motion.div>
    </div>
  )
}

export default Success
